import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionService } from '../services/permission.service';
import { PlatformConfigService } from '../services/platform-config.service';
import { APP_PATHS } from '@core/constants/platform-config.constants';
import { AuthService } from '@core/services/auth/auth.service';
import { ToastService } from '@core/services/toast.service';

/**
 * Guard to check if the user has the required permissions for a route.
 * permissions are passed via route data: { permissions: ['P1', 'P2'], permissionMatchMode: 'ALL' | 'ANY' }
 *
 * On denial, redirects to the first accessible page within the current platform
 * (determined from platform-config nav items). Falls back to the platform root.
 */
export const permissionGuard: CanActivateFn = (route, state) => {
  const permissionService = inject(PermissionService);
  const platformConfigService = inject(PlatformConfigService);
  const router = inject(Router);
  const authService = inject(AuthService);
  const toastService = inject(ToastService);

  const permissions = route.data['permissions'] as string[] | undefined;
  const matchMode = (route.data['permissionMatchMode'] as 'ALL' | 'ANY') || 'ANY';
  let hasAccess = false;
  if (!permissions || permissions.length === 0) {
    hasAccess = true;
  } else if (matchMode === 'ALL') {
    hasAccess = permissionService.hasPermission(permissions);
  } else {
    hasAccess = permissionService.hasAnyPermission(permissions);
  }

  const platformRoot = state.url.split('/')[1];

  if (!hasAccess) {
    // Redirect to the first accessible leaf route within the same platform
    const firstAccessibleRoute = platformConfigService.getFirstAccessibleRoute(platformRoot);
    if (!firstAccessibleRoute) {
       authService.logout();
      router.navigateByUrl(`/${APP_PATHS.LOGIN}`);
      toastService.showError("You do not have any permissions.", "Please contact your administrator");
    }else{
      router.navigateByUrl(firstAccessibleRoute ?? `/${platformRoot}`);
    }
    return false;
  }

  // If we are on the main inventory shell root (empty path child of inventory), 
  // try to redirect to its first accessible child to avoid a white/empty screen.
  // This specifically applies only to the landing gse/inventory path as requested.
  const currentPath = state.url.split('?')[0];
  const inventoryRoot = `/${platformRoot}/inventory`;
  
  if (route.routeConfig?.path === '' && (currentPath === inventoryRoot || currentPath === `${inventoryRoot}/`)) {
    const firstAccessibleChild = platformConfigService.getFirstAccessibleRoute(
      platformRoot,
      state.url
    );
    if (firstAccessibleChild && firstAccessibleChild !== currentPath) {
      router.navigateByUrl(firstAccessibleChild);
      return false;
    }
  }

  return true;
};
